import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import HoldingsTable from "../HoldingsTable";
import ActivityTable from "../ActivityTable";
import SummaryTab from "../SummaryTab";
import TickerDetailsPage from "../TickerDetailsPage";
import EmptyAccountState from "./EmptyAccountState";
import EditAccountModal from "../modals/EditAccountModal";
import DeleteAccountModal from "../modals/DeleteAccountModal";
import { SnapTradeAccountSettings } from "../snaptrade";
import IntelLibrary from "../IntelLibrary";
import api from "@/api";

const MainContent = ({
  selectedAccountId,
  activeTab,
  symbol,
  accounts,
  onImportComplete,
  onRefresh,
  refreshingAccountId,
  setRefreshingAccountId,
  refreshTrigger = 0,
  setRefreshTrigger,
  onAddAccount,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSnapTradeSettings, setShowSnapTradeSettings] = useState(false);

  // Check if we're on the intel library page
  const isIntelPage = location.pathname === "/intel";

  // State for account data (just for header/modals)
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(false);

  // Import status tracking
  const [importInProgress, setImportInProgress] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const pollIntervalRef = React.useRef(null);

  // Check if current account is refreshing
  const refreshingPrices = refreshingAccountId === selectedAccountId;

  // Fetch basic account details for header (skip for ticker pages - they handle their own data)
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!selectedAccountId || symbol) {
        setSelectedAccount(null);
        setImportInProgress(false);
        return;
      }

      try {
        setLoadingAccount(true);
        const accountResponse = await api.get(`/accounts/${selectedAccountId}`);
        const account = accountResponse.data;
        setSelectedAccount(account);

        console.log("[CSV Import Debug] Account data:", {
          account_id: account.id,
          account_name: account.name,
          current_import_upload_id: account.current_import_upload_id,
          has_upload_id: !!account.current_import_upload_id,
        });

        // Check if import is in progress
        if (account.current_import_upload_id) {
          console.log("[CSV Import Debug] Import in progress detected!");
          setImportInProgress(true);

          // Fetch upload status
          try {
            const uploadResponse = await api.get(
              `/accounts/${selectedAccountId}/uploads`,
            );
            const uploads = uploadResponse.data;
            const currentUpload = uploads.find(
              (u) => u.id === account.current_import_upload_id,
            );

            if (currentUpload) {
              setImportStatus(currentUpload.processing_status);

              // If completed or failed, import is done
              if (
                currentUpload.processing_status === "completed" ||
                currentUpload.processing_status === "failed"
              ) {
                setImportInProgress(false);
                // Trigger refresh to show new data
                if (onRefresh) {
                  onRefresh();
                }
              }
            }
          } catch (err) {
            console.error("Error fetching upload status:", err);
          }
        } else {
          setImportInProgress(false);
          setImportStatus(null);
        }
      } catch (error) {
        console.error("Error fetching account data:", error);
        setSelectedAccount(null);
        setImportInProgress(false);
      } finally {
        setLoadingAccount(false);
      }
    };

    fetchAccountData();
  }, [selectedAccountId, refreshTrigger, symbol]);

  // Poll for import status while in progress
  useEffect(() => {
    if (!importInProgress || !selectedAccountId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      try {
        console.log("[CSV Import Poll] Checking import status...");
        const accountResponse = await api.get(`/accounts/${selectedAccountId}`);
        const account = accountResponse.data;

        console.log(
          "[CSV Import Poll] current_import_upload_id:",
          account.current_import_upload_id,
        );

        if (!account.current_import_upload_id) {
          // Import completed!
          console.log("[CSV Import Poll] Import completed! Refreshing...");
          setImportInProgress(false);
          setImportStatus(null);
          if (setRefreshTrigger) {
            setRefreshTrigger((prev) => prev + 1);
          }
          if (onRefresh) {
            console.log("[CSV Import Poll] Calling onRefresh()");
            onRefresh();
          }
          // Re-fetch account data to show updated values
          const updatedAccount = await api.get(
            `/accounts/${selectedAccountId}`,
          );
          console.log(
            "[CSV Import Poll] Updated account data:",
            updatedAccount.data,
          );
          setSelectedAccount(updatedAccount.data);
          return;
        }

        // Check upload status
        const uploadResponse = await api.get(
          `/accounts/${selectedAccountId}/uploads`,
        );
        const uploads = uploadResponse.data;
        const currentUpload = uploads.find(
          (u) => u.id === account.current_import_upload_id,
        );

        if (currentUpload) {
          console.log(
            "[CSV Import Poll] Upload status:",
            currentUpload.processing_status,
          );
          setImportStatus(currentUpload.processing_status);

          if (currentUpload.processing_status === "completed") {
            console.log("[CSV Import Poll] Status is completed, refreshing...");
            setImportInProgress(false);
            if (setRefreshTrigger) {
              setRefreshTrigger((prev) => prev + 1);
            }
            if (onRefresh) {
              console.log("[CSV Import Poll] Calling onRefresh()");
              onRefresh();
            }
            // Re-fetch account data
            const updatedAccount = await api.get(
              `/accounts/${selectedAccountId}`,
            );
            console.log(
              "[CSV Import Poll] Updated account data:",
              updatedAccount.data,
            );
            setSelectedAccount(updatedAccount.data);
          } else if (currentUpload.processing_status === "failed") {
            console.log(
              "[CSV Import Poll] Import failed:",
              currentUpload.processing_error,
            );
            setImportInProgress(false);
            console.error("Import failed:", currentUpload.processing_error);
          }
        }
      } catch (err) {
        console.error("[CSV Import Poll] Error polling import status:", err);
      }
    }, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [importInProgress, selectedAccountId, onRefresh]);

  // Get the title based on selection
  const title = selectedAccount ? selectedAccount.name : "All Accounts";

  // Helper to build tab URL
  const getTabUrl = (tab) => {
    if (selectedAccountId) {
      return tab === "summary"
        ? `/account/${selectedAccountId}`
        : `/account/${selectedAccountId}/${tab}`;
    }
    return tab === "summary" ? "/" : `/${tab}`;
  };

  // True while a brokerage sync (Plaid/SnapTrade/etc.) is running
  const syncInProgress = selectedAccount?.sync_in_progress === true;

  // Check if account is empty (specific account selected and it's manual with no value)
  // Only show empty state for MANUAL accounts - connected accounts show tabs with loading
  // Don't show empty state while importing or syncing
  const isEmptyAccount =
    selectedAccountId &&
    selectedAccount &&
    selectedAccount.source !== "PLAID" &&
    selectedAccount.source !== "SNAPTRADE" &&
    !loadingAccount &&
    !importInProgress &&
    !syncInProgress &&
    (selectedAccount.total_account_value === 0 ||
      selectedAccount.total_account_value === null);

  const handleSaveAccount = async (data) => {
    await api.put(`/accounts/${selectedAccountId}`, data);

    // Re-fetch account data
    const accountResponse = await api.get(`/accounts/${selectedAccountId}`);
    setSelectedAccount(accountResponse.data);

    if (onRefresh) {
      onRefresh();
    }
  };

  const handleDeleteAccount = async () => {
    try {
      console.log("🗑️ Deleting account:", selectedAccountId);
      await api.delete(`/accounts/${selectedAccountId}`);
      console.log("✅ Account deleted successfully");

      // Navigate first to avoid rendering issues
      navigate("/");

      // Increment refresh trigger to force sidebar update
      if (setRefreshTrigger) {
        setRefreshTrigger((prev) => prev + 1);
      }

      // Then refresh sidebar to update account list
      if (onRefresh) {
        console.log("🔄 Calling onRefresh after delete...");
        await onRefresh();
        console.log("✅ onRefresh completed");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert(
        `Failed to delete account: ${
          error.response?.data?.detail || error.message
        }`,
      );
    }
  };

  const handleRefresh = async () => {
    if (!selectedAccountId || refreshingPrices) return;

    try {
      setRefreshingAccountId(selectedAccountId);

      // Unified refresh endpoint handles both Plaid and manual accounts
      await api.post(`/accounts/${selectedAccountId}/refresh`);

      // Refresh parent to update sidebar
      if (onRefresh) {
        onRefresh();
      }

      // Refresh tab content (positions, activity, summary)
      if (setRefreshTrigger) {
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error refreshing account:", error);
    } finally {
      setRefreshingAccountId(null);
    }
  };

  // If we're on the intel page, render the intel library instead
  if (isIntelPage) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Intel Library
          </h2>
        </div>

        {/* Intel Library Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800">
          <IntelLibrary />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Modals */}
      {showEditModal && (
        <EditAccountModal
          account={selectedAccount}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveAccount}
        />
      )}
      {showDeleteModal && (
        <DeleteAccountModal
          account={selectedAccount}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDeleteAccount}
        />
      )}
      {showSnapTradeSettings && (
        <SnapTradeAccountSettings
          accountId={selectedAccountId}
          onClose={() => setShowSnapTradeSettings(false)}
          onUpdate={() => {
            setShowSnapTradeSettings(false);
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {/* Show ticker details if symbol is present */}
      {symbol ? (
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-800">
          <TickerDetailsPage accounts={accounts} />
        </div>
      ) : (
        <>
          {/* Header with Title and Actions */}
          <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {title}
              </h2>

              {/* All Accounts view: quick "add account" action */}
              {!selectedAccountId && onAddAccount && (
                <button
                  onClick={onAddAccount}
                  className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  title="Add account"
                  aria-label="Add account"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              )}

              {/* Show edit/delete icons only for specific accounts */}
              {selectedAccountId && (
                <div className="flex items-center gap-2">
                  {/* Refresh Button - Backend handles Plaid sync or price refresh */}
                  <button
                    onClick={handleRefresh}
                    disabled={refreshingPrices}
                    className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh account"
                  >
                    <svg
                      className={`w-5 h-5 ${
                        refreshingPrices ? "animate-spin" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>

                  {/* SnapTrade settings — only for SNAPTRADE-sourced accounts */}
                  {selectedAccount?.source === "SNAPTRADE" && (
                    <button
                      onClick={() => setShowSnapTradeSettings(true)}
                      className="p-2 text-gray-600 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      title="SnapTrade settings"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                  )}

                  <button
                    onClick={() => setShowEditModal(true)}
                    className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="Edit account"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="Delete account"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Import / Sync Progress Banner */}
          {(importInProgress || syncInProgress) && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <div>
                  <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {syncInProgress
                      ? "Syncing account — importing holdings and transaction history..."
                      : "CSV Import in Progress - Processing transactions and creating historical snapshots..."}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* While importing or syncing, hide tabs */}
          {importInProgress || syncInProgress ? (
            <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-800"></div>
          ) : isEmptyAccount ? (
            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800">
              <div className="p-6">
                <EmptyAccountState />
              </div>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="flex px-6">
                  <Link
                    to={getTabUrl("summary")}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "summary"
                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    Summary
                  </Link>
                  <Link
                    to={getTabUrl("positions")}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "positions"
                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    Positions
                  </Link>
                  <Link
                    to={getTabUrl("activity")}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "activity"
                        ? "border-blue-500 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    Activity
                  </Link>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800">
                <div className="p-6">
                  {activeTab === "summary" && (
                    <SummaryTab
                      accountId={selectedAccountId}
                      refreshTrigger={refreshTrigger}
                    />
                  )}{" "}
                  {activeTab === "positions" && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Positions
                      </h3>
                      <HoldingsTable
                        accountId={selectedAccountId}
                        refreshTrigger={refreshTrigger}
                      />
                    </div>
                  )}
                  {activeTab === "activity" && (
                    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Activity & Orders
                      </h3>
                      <ActivityTable
                        accountId={selectedAccountId}
                        refreshTrigger={refreshTrigger}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MainContent;
